import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { FaArrowLeft } from 'react-icons/fa';
import { axiosInstance as axios } from '../../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { TemplateChooser } from './TemplateChooser';
import { TaskChecklist } from './TaskChecklist';
import { dueDateFor } from './templateUtils';

export const appliedMessage = ({ count, skipped }, eventName) => {
  const dup = skipped ? ` (${skipped} already on the list, skipped)` : '';
  return count ? `Added ${count} planning task${count === 1 ? '' : 's'} to "${eventName}"${dup}` : `No new tasks added${dup}`;
};

export function TemplatePickerModal({ event, familyId, onClose, onApplied, reopened = false }) {
  const [templates, setTemplates] = useState({ builtin: [], saved: [] });
  const [loading, setLoading] = useState(true);
  const [chosen, setChosen] = useState(null);
  const [selected, setSelected] = useState([]);
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`/api/families/${familyId}/event-templates`);
      setTemplates(res.data);
    } catch {
      toast.error('Could not load templates');
    } finally {
      setLoading(false);
    }
  }, [familyId]);

  useEffect(() => { load(); }, [load]);

  const choose = (t) => { setChosen(t); setSelected(t.tasks.map((_, i) => i)); };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete the "${t.name}" template for everyone in the family?`)) return;
    try {
      await axios.delete(`/api/event-templates/${t.template_id}`);
      toast.success('Template deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete template');
    }
  };

  const apply = async () => {
    setApplying(true);
    const tasks = [...selected].sort((a, b) => a - b).map((i) => {
      const t = chosen.tasks[i];
      return { title: t.title, category: t.category, description: t.description || null, due_date: dueDateFor(event.start_date, t.days_before) };
    });
    try {
      const res = await axios.post(`/api/events/${event.event_id}/apply-template`, { tasks });
      onApplied(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add tasks');
      setApplying(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-picker-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl">{chosen ? chosen.name : reopened ? 'Add tasks from a template' : 'Start with a planning checklist?'}</DialogTitle>
          <DialogDescription>
            {chosen
              ? `Untick anything you don't need. Due dates count back from ${event.name}'s date and can be changed later.${reopened ? ' Tasks already on this event are skipped.' : ''}`
              : `Pick an event type and we'll suggest the tasks for "${event.name}".`}
          </DialogDescription>
        </DialogHeader>

        {!chosen ? (
          <TemplateChooser {...templates} loading={loading} onSelect={choose} onDelete={handleDelete} onSkip={onClose} skipLabel={reopened ? 'Cancel' : undefined} />
        ) : (
          <>
            <TaskChecklist tasks={chosen.tasks} selected={selected} setSelected={setSelected} startDate={event.start_date} />
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setChosen(null)} className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" data-testid="back-to-templates-btn">
                <FaArrowLeft className="text-xs" /> Back
              </button>
              <button
                type="button"
                onClick={apply}
                disabled={applying || selected.length === 0}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 font-semibold transition-colors"
                data-testid="apply-template-btn"
              >
                {applying ? 'Adding...' : `Add ${selected.length} task${selected.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
